<template>
  <div> <div class="modal fade" ref="modalElement" id="review-modal" tabindex="-1" aria-labelledby="reviewModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="reviewModalLabel">{{ dialogTitle }}</h5>
          <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form id="review-form" class="p-3" @submit.prevent="submitReview">
            <div class="mb-3">
              <label id="rating-label" class="form-label">Rating?</label>
              <div class="star-rating" aria-labelledby="rating-label">
                <template v-for="n in 5" :key="n">
                  <input type="radio" class="btn-check" name="rating" :id="'rating' + n" :value="6 - n" v-model="rating" :disabled="loading || uploading" autocomplete="off">
                  <label class="btn btn-outline-warning star-label" :for="'rating' + n" :class="{ 'active': rating >= n }">
                    <i class="bi bi-star-fill"></i> </label>
                </template>
              </div>
            </div>

            <div class="mb-3">
              <label for="input-2" class="form-label">Any comments? (optional)</label>
              <textarea
                class="form-control"
                id="input-2"
                v-model="body"
                placeholder="Talk about your hike..."
                rows="3"
                style="max-height: calc(1.5em * 6 + 0.75rem + 2px); overflow-y: auto;"
              :disabled="loading || uploading"
              ></textarea>
            </div>

            <div class="mb-3">
              <label for="photosInput" class="form-label">Add a photo or two? (optional)</label>
              <input type="file"
                     class="form-control"
                     id="photosInput"
                     multiple
                     :disabled="loading || uploading"
                     @change="fileInputEvent"
                     ref="photosInput"
                     accept="image/jpeg, image/png">
            </div>

            <div v-if="editingReview && editingReview.photos && editingReview.photos.length > 0" class="mb-3">
              <label class="form-label">Current Photos:</label>
              <div v-for="photo in editingReview.photos" :key="photo.id" class="d-flex align-items-center mb-2">
                <img :src="photo.lgUrl"
                     alt="Review photo"
                     style="width: 64px; height: 64px; object-fit: cover; object-position: center; margin-right: 10px; border: 1px solid #dee2e6;"/>
                <button @click.prevent="destroyPhoto(photo)" class="btn btn-sm btn-outline-danger d-flex align-items-center">
                  <span v-if="destroyingPhotoIds.includes(photo.id)" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  <span v-else class="material-icons me-1 fs-6">delete</span>
                  Delete
                </button>
              </div>
            </div>

            <div v-if="files && files.length > 0" class="mb-3">
              <label class="form-label">New Photos:</label>
              <div class="card row g-0 m-0 mb-2 py-2 align-items-center" style="flex-direction: row;" v-for="file in files" :key="file.firebaseRefFullPath">
                <div class="col-5 d-flex align-items-center">
                  <small class="text-truncate" :title="file.name">{{ file.name }} - {{ Math.round(file.progress) }}%</small>
                </div>
                <div class="col-4 d-flex align-items-center justify-content-center">
                  <div v-if="file.progress > 0 && file.progress < 100" class="progress w-100" style="height: 10px;">
                    <div class="progress-bar" role="progressbar" :style="{ width: file.progress + '%' }" :aria-valuenow="file.progress" aria-valuemin="0" aria-valuemax="100"></div>
                  </div>
                  <img v-if="file.photoUrl"
                       class="square"
                       alt="Uploaded preview"
                       :src="file.photoUrl"
                       style="width: 40px; height: 40px; object-fit: cover; border: 1px solid #dee2e6;"/>
                </div>
                <div class="col-3 d-flex align-items-center justify-content-end">
                  <button @click.prevent="removeFile(file)" class="btn btn-sm btn-secondary d-flex align-items-center">
                    <span class="material-icons fs-6">delete</span>
                  </button>
                </div>
              </div>
            </div>


            <div class="d-flex justify-content-end mt-4">
              <button id="resetButton"
                      type="button" class="btn btn-secondary me-2"
                      :disabled="loading || uploading"
                      @click="closeModal">
                Cancel
              </button>
              <button id="submit-button"
                      type="submit" class="btn btn-primary"
                      :disabled="loading || uploading || !rating"> <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import { ref, storage, uploadBytesResumable, getDownloadURL } from '../../utils/firebase/storage';
import { mapState } from 'vuex';
import { Modal } from 'bootstrap'; // Import Bootstrap's Modal component

export default {
  // No BootstrapVue components needed
  data() {
    return {
      bootstrapModal: null, // To hold the Bootstrap Modal instance
    };
  },
  computed: {
    ...mapState(['editingReview', 'files', 'photoUrls', 'destroyingPhotoIds', 'clientId', 'loading', 'uploading']),
    body: {
      get() {
        return this.$store.state.body;
      },
      set(value) {
        this.$store.dispatch('setBody', value);
      },
    },
    rating: {
      get() {
        // Ensure rating is a number, default to 0 if null/undefined initially
        const val = this.$store.state.rating;
        return typeof val === 'number' ? val : 0;
      },
      set(value) {
        // Ensure value passed is a number
        this.$store.dispatch('setRating', Number(value));
      },
    },
    showModal: {
      // This computed property now primarily *reads* the state
      get() {
        return this.$store.state.showModal;
      },
      // We don't define a setter here; we control visibility via watcher and methods
    },
    dialogTitle() {
      return this.editingReview ? `Edit Review ${this.editingReview.id}` : 'Add Review';
    },
  },
  watch: {
    // Watch the Vuex state and show/hide the Bootstrap modal accordingly
    showModal(newValue) {
      if (!this.bootstrapModal) return;
      if (newValue) {
        this.bootstrapModal.show();
      } else {
        this.bootstrapModal.hide();
      }
    },
  },
  mounted() {
    // Initialize the Bootstrap modal once the component is mounted
    const modalElement = this.$refs.modalElement;
    this.bootstrapModal = new Modal(modalElement);

    // Optional: Sync Vuex state if the modal is closed by clicking backdrop or pressing Esc
    modalElement.addEventListener('hidden.bs.modal', () => {
      if (this.showModal) { // Only update state if it was supposed to be open
        this.$store.dispatch('setShowModal', false);
      }
      // Reset file input when modal closes completely
      this.resetFileInput();
    });

    // Optional: Reset file input if shown again after being hidden
    modalElement.addEventListener('show.bs.modal', () => {
      this.resetFileInput();
    });

    // If the modal should be shown immediately based on initial state
    if (this.showModal) {
      this.bootstrapModal.show();
    }
  },
  beforeUnmount() {
    // Clean up the modal instance and event listeners
    if (this.bootstrapModal) {
      const modalElement = this.$refs.modalElement;
      modalElement.removeEventListener('hidden.bs.modal', this.handleModalHidden); // Use a named function if needed
      modalElement.removeEventListener('show.bs.modal', this.resetFileInput);
      this.bootstrapModal.dispose(); // Dispose of the Bootstrap modal instance
    }
  },
  methods: {
    // handleModalHidden() { // Example if needing a named function for removal
    //   if (this.showModal) {
    //      this.$store.dispatch('setShowModal', false);
    //   }
    //   this.resetFileInput();
    // },
    resetFileInput() {
      const fileInput = this.$refs.photosInput;
      if (fileInput) {
        fileInput.value = '';
      }
    },
    destroyPhoto(photo, event) {
      // event.preventDefault(); // No longer needed if button type="button" or handled in @click.prevent
      this.$store.dispatch('destroyPhoto', photo);
    },
    removeFile(file, event) {
      // event.preventDefault(); // No longer needed if button type="button" or handled in @click.prevent
      this.$store.dispatch('removeFile', file);
    },
    submitReview() {
      // Basic validation example: ensure rating is selected
      if (!this.rating) {
        alert('Please select a rating.'); // Replace with better UX if desired
        return;
      }
      this.$store.dispatch('submitReview');
      // Modal hiding should ideally be handled *after* successful submission
      // in the Vuex action or by watching a 'submissionSuccess' state.
      // For now, we assume the action might set showModal to false.
    },
    closeModal() {
      // Update the Vuex state, the watcher will handle hiding the modal
      this.$store.dispatch('setShowModal', false);
      // No need to call this.bootstrapModal.hide() directly due to the watcher
      // File input reset is now handled by the 'hidden.bs.modal' event listener
    },
    async fileInputEvent() {
      const vm = this;
      const filesArray = Array.from(document.getElementById('photosInput').files);
      filesArray.forEach((f) => {
        f.progress = 0;
        vm.files.push(f);
      });

      for (const file of filesArray) {
        try {
          this.$store.dispatch('setUploading', true);
          const userId = window.user.firebase_user_id;
          const firebaseRefEnding = `web------${userId}---review---${vm.clientId}---${file.name}------${new Date().toISOString()}.jpg`;
          const myRef = ref(storage, 'rails_upload_end_point/' + firebaseRefEnding);

          file.firebaseRefFullPath = myRef.fullPath;
          file.progress = 0;


          const uploadTask = uploadBytesResumable(myRef, file)

          // Wrap the state_changed event in a Promise
          await new Promise((resolve, reject) => {
            uploadTask.on('state_changed', (snapshot) => {
              let progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              if (progress.toString() === 'NaN') {
                progress = 0;
              }

              vm.files.find((f) => f.firebaseRefFullPath === snapshot.ref.fullPath).progress = progress;
              vm.$nextTick(() => {
                vm.$forceUpdate();
              });
            }, reject, resolve);
          });

          // Get the download URL after upload is complete
          const downloadURL = await getDownloadURL(myRef);
          file.photoUrl = downloadURL;
          vm.photoUrls.push(downloadURL);

          if (vm.files.length === vm.photoUrls.length) {
            this.$store.dispatch('setUploading', false);
            document.getElementById('photosInput').value = '';
          }
        } catch (error) {
          console.log(error);
        }
      }
    },
  },
};
</script>

<style lang="scss">

// Keep existing styles if they don't conflict heavily
// #review-form { ... }
// #review-modal .modal-header { ... } // Adjust selectors if needed

// Custom Star Rating Styles
.star-rating {
  display: inline-flex; // Or flex
  direction: rtl; // To fill stars left-to-right on hover/selection
  justify-content: flex-end; // Align stars to the right initially
}

.star-label {
  font-size: 1.5rem; // Adjust size as needed (like size="lg")
  color: #adb5bd; // Default star color (empty)
  cursor: pointer;
  padding: 0 0.15em; // Adjust spacing
  transition: color 0.2s ease-in-out, transform 0.15s ease-in-out;
  border: none !important; // Remove default btn border
  background-color: transparent !important; // Remove default btn background
  box-shadow: none !important; // Remove default btn shadow
  line-height: 1; // Ensure icons align well

  // Override btn-outline-warning focus/active styles
  &:focus, &:active {
    box-shadow: none !important;
  }

  // Hide the icon inside the label initially
  .bi {
    // Start empty
    color: #adb5bd;
  }
}

// When the radio input is checked, style its corresponding label
input.btn-check:checked ~ .star-label {
  .bi { color: #ffc107; } // Filled star color (warning)
}


// Hover effect: Change color of the hovered star and stars to its "left" (due to rtl)
.star-rating:not(:disabled) .star-label:hover,
.star-rating:not(:disabled) .star-label:hover ~ .star-label {
  .bi { color: #ffca2c; } // Slightly brighter hover color
  transform: scale(1.2); // Scale effect like b-rating
}

// Style the specifically checked label's icon
input.btn-check:checked + .star-label {
  .bi { color: #ffc107; } // Ensure selected is warning color
}

// When disabled, reset cursor and potentially colors
input.btn-check:disabled + .star-label {
  cursor: default;
  // Optional: Adjust opacity or color for disabled state
  // opacity: 0.65;
  .bi {
    color: #adb5bd; // Keep disabled stars gray
  }
}
input.btn-check:disabled:checked + .star-label {
  .bi {
    color: #ffc107; // Keep checked disabled stars yellow
    opacity: 0.65;
  }
}


// Ensure text area doesn't overflow modal badly
#input-2 {
  min-height: calc(1.5em * 3 + 0.75rem + 2px); // Corresponds to rows="3"
}

// Style for file preview cards
.card.row {
  border: 1px solid #dee2e6;
}

.square {
  // Style for preview images if needed
}

// Ensure material icons align well with buttons
.material-icons.fs-6 {
  font-size: 1rem !important; // Match button text size better
  vertical-align: text-bottom;
}

// Hide default file input text if needed (Bootstrap usually handles this)
// .form-control[type="file"]:not(:disabled):not([readonly]) { ... }

</style>
